import { c as _c } from "react/compiler-runtime";
import React from "react";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import type { CollectedCard } from "@/entities/IProfile";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { Time } from "@/helpers/time";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useUserPrevoteCards } from "@/hooks/useUserPrevoteCards";
import { useUserWinningArtworks } from "@/hooks/useUserWinningArtworks";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { faEye, faTrophy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import { Tooltip } from "react-tooltip";
import { ArtistPrevoteCollectedCard } from "./ArtistPrevoteCollectedCard";
interface ArtistWinningArtworksContentProps {
  readonly user: ApiProfileMin;
  readonly isOpen: boolean;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
type TrophyItem = {
  readonly type: "winner";
  readonly key: string;
  readonly drop: ApiDrop;
} | {
  readonly type: "prevote";
  readonly key: string;
  readonly card: CollectedCard;
};
const WinnerTrophyCard: React.FC<{
  readonly drop: ApiDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}> = t0 => {
  const $ = _c(52);
  const {
    drop,
    onDropClick
  } = t0;
  let t1;
  if ($[0] !== drop) {
    t1 = convertApiDropToExtendedDrop(drop);
    $[0] = drop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const extendedDrop = t1;
  const decisionTime = drop.winning_context?.decision_time;
  let t2;
  if ($[2] !== extendedDrop || $[3] !== onDropClick) {
    t2 = () => onDropClick(extendedDrop);
    $[2] = extendedDrop;
    $[3] = onDropClick;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== drop.parts[0]) {
    t3 = <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">{drop.parts[0]?.media && drop.parts[0].media.length > 0 ? <MediaDisplay media_url={drop.parts[0].media[0].url} media_mime_type={drop.parts[0].media[0].mime_type} disableMediaInteraction={true} /> : <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-bg-iron-950 tw-p-4"><p className="tw-line-clamp-6 tw-text-center tw-text-sm tw-text-iron-300">{drop.parts[0]?.content ?? "No content"}</p></div>}</div>;
    $[5] = drop.parts[0];
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="tw-absolute tw-right-3 tw-top-3 tw-opacity-0 tw-transition-opacity tw-duration-300 desktop-hover:group-hover:tw-opacity-100"><div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-black/50 tw-backdrop-blur-sm"><FontAwesomeIcon icon={faEye} className="tw-h-3.5 tw-w-3.5 tw-text-white" /></div></div>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3) {
    t5 = <div className="tw-relative tw-aspect-square tw-overflow-hidden tw-bg-iron-950/50">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== drop.title) {
    t6 = drop.title && <p className="tw-mb-0 tw-truncate tw-text-sm tw-font-bold tw-leading-tight tw-text-iron-100">{drop.title}</p>;
    $[10] = drop.title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <div className="tw-mb-3 tw-flex tw-items-start tw-justify-between"><div className="tw-mr-2 tw-min-w-0 tw-flex-1">{t6}</div></div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== drop.rating) {
    t8 = formatNumberWithCommas(drop.rating);
    $[14] = drop.rating;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-text-sm"><span className="tw-font-medium tw-text-[#D4AF37]">{t8}</span></div>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== drop.wave.voting_credit_type) {
    t10 = <div className="tw-whitespace-nowrap tw-text-sm tw-text-iron-500"><span className="tw-font-medium">{drop.wave.voting_credit_type} total</span></div>;
    $[18] = drop.wave.voting_credit_type;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className="tw-mb-3"><div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5">{t9}{t10}</div></div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== drop.id || $[24] !== drop.top_raters) {
    let t13;
    if ($[26] !== drop.id) {
      t13 = voter => <div key={voter.profile.handle}><Link href={`/${voter.profile.handle ?? voter.profile.primary_address}`}>{voter.profile.pfp ? <Image className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800 tw-object-contain" src={getScaledImageUri(voter.profile.pfp, ImageScale.W_AUTO_H_50)} alt={voter.profile.handle ?? voter.profile.primary_address} width={24} height={24} data-tooltip-id={`winning-voter-${drop.id}-${voter.profile.handle ?? voter.profile.primary_address}`} /> : <div className="tw-h-6 tw-w-6 tw-rounded-md tw-border-2 tw-border-solid tw-border-[#111] tw-bg-iron-800" data-tooltip-id={`winning-voter-${drop.id}-${voter.profile.handle ?? voter.profile.primary_address}`} />}</Link><Tooltip id={`winning-voter-${drop.id}-${voter.profile.handle ?? voter.profile.primary_address}`} style={{
          backgroundColor: "#1F2937",
          color: "white",
          padding: "4px 8px"
        }}>{voter.profile.handle} -{" "}{formatNumberWithCommas(voter.rating)}</Tooltip></div>;
      $[26] = drop.id;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    t12 = drop.top_raters.slice(0, 3).map(t13);
    $[23] = drop.id;
    $[24] = drop.top_raters;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <div className="tw-flex tw-items-center -tw-space-x-2">{t12}</div>;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== drop.raters_count) {
    t14 = formatNumberWithCommas(drop.raters_count);
    $[30] = drop.raters_count;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = drop.raters_count === 1 ? "voter" : "voters";
  let t16;
  if ($[32] !== t15) {
    t16 = <span className="tw-font-normal tw-text-iron-500">{t15}</span>;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t14 || $[35] !== t16) {
    t17 = <span className="tw-text-xs tw-font-bold tw-text-white">{t14}{" "}{t16}</span>;
    $[34] = t14;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t13 || $[38] !== t17) {
    t18 = <div className="tw-flex tw-items-center tw-gap-2">{t13}{t17}</div>;
    $[37] = t13;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== decisionTime || $[41] !== drop.created_at) {
    t19 = <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-iron-500">{typeof decisionTime === "number" ? <><FontAwesomeIcon icon={faTrophy} className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-[#D4AF37]" /><span>Won on{" "}{Time.millis(decisionTime).toDate().toLocaleDateString()}</span></> : <><CalendarDaysIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /><span>{Time.millis(drop.created_at).toDate().toLocaleDateString()}</span></>}</div>;
    $[40] = decisionTime;
    $[41] = drop.created_at;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t11 || $[44] !== t18 || $[45] !== t19 || $[46] !== t7) {
    t20 = <div className="tw-flex tw-flex-1 tw-flex-col tw-justify-between tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-bg-iron-950/50 tw-p-3">{t7}{t11}{t18}{t19}</div>;
    $[43] = t11;
    $[44] = t18;
    $[45] = t19;
    $[46] = t7;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t2 || $[49] !== t20 || $[50] !== t5) {
    t21 = <div data-testid="trophy-item-winner" className="tw-flex tw-h-full tw-flex-col"><div className="tw-group tw-relative tw-flex tw-flex-1 tw-cursor-pointer tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950/50 tw-shadow-lg tw-transition-all tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-700 desktop-hover:hover:tw-shadow-xl" onClick={t2}>{t5}{t20}</div></div>;
    $[48] = t2;
    $[49] = t20;
    $[50] = t5;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
};
export const ArtistWinningArtworksContent: React.FC<ArtistWinningArtworksContentProps> = t0 => {
  const $ = _c(18);
  const {
    user,
    isOpen,
    onDropClick
  } = t0;
  let t1;
  if ($[0] !== isOpen || $[1] !== user) {
    t1 = {
      user,
      enabled: isOpen
    };
    $[0] = isOpen;
    $[1] = user;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    winningDrops,
    isLoading: isWinningDropsLoading
  } = useUserWinningArtworks(t1);
  let t2;
  if ($[3] !== isOpen || $[4] !== user) {
    t2 = {
      user,
      enabled: isOpen
    };
    $[3] = isOpen;
    $[4] = user;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    prevoteCards,
    isLoading: isPrevoteCardsLoading
  } = useUserPrevoteCards(t2);
  const isLoading = isWinningDropsLoading || isPrevoteCardsLoading;
  let t3;
  if ($[6] !== prevoteCards || $[7] !== winningDrops) {
    t3 = [...winningDrops.map(_temp), ...prevoteCards.map(_temp2)];
    $[6] = prevoteCards;
    $[7] = winningDrops;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const trophyItems = t3;
  if (isLoading) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-flex tw-h-96 tw-items-center tw-justify-center"><div className="tw-flex tw-flex-col tw-items-center tw-gap-4"><div className="tw-h-8 tw-w-8 tw-animate-spin tw-rounded-full tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-amber-400" /><span className="tw-animate-fade-in-out tw-text-sm tw-text-iron-400">Loading minted memes...</span></div></div>;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t4;
  if ($[10] !== onDropClick || $[11] !== trophyItems) {
    t4 = trophyItems.length > 0 && <div className="tw-grid tw-grid-cols-1 tw-gap-6 sm:tw-grid-cols-2 lg:tw-grid-cols-3">{trophyItems.map(item => item.type === "winner" ? <WinnerTrophyCard key={item.key} drop={item.drop} onDropClick={onDropClick} /> : <div key={item.key} data-testid="trophy-item-prevote" className="tw-relative tw-flex tw-h-full tw-flex-col"><ArtistPrevoteCollectedCard card={item.card} /></div>)}</div>;
    $[10] = onDropClick;
    $[11] = trophyItems;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== trophyItems.length) {
    t5 = trophyItems.length === 0 && <div className="tw-flex tw-h-72 tw-items-center tw-justify-center"><p className="tw-mb-0 tw-text-sm tw-text-iron-400">No minted memes found.</p></div>;
    $[13] = trophyItems.length;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <div className="tw-relative tw-z-[100] tw-max-h-[calc(75vh-120px)] tw-overflow-y-auto tw-p-6 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-max-h-[calc(90vh-140px)]">{t4}{t5}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
function _temp(drop) {
  return {
    type: "winner" as const,
    key: `winner-${drop.id}`,
    drop
  };
}
function _temp2(card) {
  return {
    type: "prevote" as const,
    key: `artist-prevote-card-${card.token_id}`,
    card
  };
}